import { useState, useRef } from "react";
import { Calendar, Loader2, CheckCircle, ArrowRight, Mail } from "lucide-react";
import { toast } from "sonner";
import {
  CTA,
  Panel,
  Reveal,
  RevealItem,
  Rule,
  Section,
  SectionHeader,
} from "@/components/system";

/* ═══════════════════════════════════════════════════════════════════════════
   Contact — used on ~10 routes, so every fix here lands ten times.

   Accessibility work from the previous pass (kept):
   · Labels are real <label htmlFor> elements, so clicking one focuses its
     input and screen readers announce the field.
   · autoComplete on every field, so browsers can fill name/email/company.
   · Errors are inline, tied to the field with aria-describedby, and the first
     invalid field takes focus on submit.

   Design migration, and what was wrong:
   · The labels were 10px mono caps. One of them is a seven-word question
     ("What are you trying to grow?") — that is a prompt, not a field tag, and
     it was set smaller than everything it was asking about. All four are now
     body-size sentence case.
   · Gold was doing four jobs in this one section: the required asterisk, the
     input focus ring, the 48px success tick and the submit button. Only the
     last of those is clickable. Gold now appears here on clickable things
     only; the focus ring is the site's signal outline (index.css), which is
     what focus — a state report, not an action — is supposed to look like.
   · The placeholders read as filled values: "Priya Sharma", "priya@company.com"
     in an email field with autoComplete="email" (visually identical to an
     autofill), and "Pocket FM" — a client the visitor had just scrolled past,
     so on the Pocket FM case study the form looked pre-filled with the page's
     own subject. They are short hints now, on one convention.
   · The panel was `p-12` when submitted and `p-6 md:p-8` otherwise, so it
     jumped size at the moment of success. Both states are <Panel> now.
   ═══════════════════════════════════════════════════════════════════════════ */

const DEFAULT_BULLETS = [
  "Send a link to your product or app, and the audience you want",
  "We shape one angle and make one finished ad concept, on us",
  "First cut in 48 hours. If it works, we build the system around it",
];

const EMPTY = { name: "", email: "", company: "", budget: "", interest: "", brief: "" };

/* Inputs sit on `surface` inside an unfilled panel on the ground, so the stack
   reads outward-in. It used to be inverted: the darkest fill was the input,
   nested inside a lighter card, on a darker section.

   No focus override. The global :focus-visible rule in index.css draws a
   signal outline; the old rule here killed that outline and drew a gold ring
   instead, putting a second gold object on screen at the moment of focus.
   Hover BRIGHTENS the edge — `hover:border-line/80` was making an already dim
   border dimmer, which is backwards for a control you are about to type in. */
const fieldClass =
  "w-full border border-line bg-surface p-4 font-body text-body text-bone " +
  "placeholder:text-faint transition-colors duration-hover ease-gate " +
  "hover:border-faint aria-[invalid=true]:border-alert";

/* Form labels are not the mono label style. That style is reserved for section
   eyebrows and data captions; using it here turned "Name" into "N A M E". */
const labelClass = "block font-body text-body text-bone";

function Field({ id, label, error, required, children, hint }) {
  return (
    <div className="space-y-2">
      <label htmlFor={id} className={labelClass}>
        {label}
        {/* Neutral. A required marker is not something you can click, and gold
            on a non-clickable thing is exactly how gold stopped meaning
            "click here" on this site. */}
        {required && <span aria-hidden="true" className="ml-1 text-mute">*</span>}
      </label>
      {children}
      {hint && !error && (
        <p id={`${id}-hint`} className="font-body text-caption text-mute">{hint}</p>
      )}
      {error && (
        <p id={`${id}-error`} className="font-body text-caption text-alert">{error}</p>
      )}
    </div>
  );
}

export const Contact = ({
  source = "homepage",
  headline = "Get your free sample.",
  headlineAccent = "First cut in 48 hours.",
  bullets = DEFAULT_BULLETS,
}) => {
  const [formData, setFormData] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setSubmitting] = useState(false);
  const [isSubmitted, setSubmitted] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  const formRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear the error as soon as the field is being corrected, rather than
    // making the visitor re-submit to find out they fixed it.
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  const validate = () => {
    const next = {};
    if (!formData.name.trim()) next.name = "Enter your name.";
    if (!formData.email.trim()) next.email = "Enter your email address.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      next.email = "That email address looks incomplete. Check for a typo.";
    // The brief is optional: the free sample needs an email and a product, and
    // a required paragraph was the form asking for an engagement, not a sample.
    return next;
  };

  // If the form service is down, hand the visitor a prefilled email so the lead survives.
  const fallbackMailto = () => {
    const subject = encodeURIComponent(`New Brief from ${formData.name}, ${formData.company} [${source}]`);
    const body = encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\nCompany: ${formData.company}\nBudget: ${formData.budget}\nLooking for: ${formData.interest}\n\n${formData.brief}`
    );
    return `mailto:contact@kyrosity.ai?subject=${subject}&body=${body}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (e.target.elements.botcheck?.checked) return;

    const found = validate();
    if (Object.keys(found).length) {
      setErrors(found);
      // Move the caret to the first thing that needs fixing.
      const firstId = ["name", "email"].find((k) => found[k]);
      formRef.current?.querySelector(`#contact-${firstId}`)?.focus();
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({
          access_key: "f92ed327-0079-46d5-95e2-0532f18823d7",
          subject: `New Brief from ${formData.name}, ${formData.company} [${source}]`,
          source,
          botcheck: false,
          ...formData,
        }),
      });
      if (res.ok) {
        setSubmitted(true);
        setSubmitFailed(false);
        toast.success("Received. We'll reply within 24 hours and send your first cut within 48.");
        setFormData(EMPTY);
      } else throw new Error(`HTTP ${res.status}`);
    } catch (err) {
      window.posthog?.capture?.("contact_form_failed", { source, error: String(err?.message || err) });
      setSubmitFailed(true);
      toast.error("Couldn't send your brief. Use the email option below, where your message is prefilled.");
    } finally {
      setSubmitting(false);
    }
  };

  const describedBy = (id, hint) =>
    errors[id.replace("contact-", "")] ? `${id}-error` : hint ? `${id}-hint` : undefined;

  return (
    <Section
      id="contact"
      tone="deep"
      data-reel-label="Contact"
      data-testid="contact-section"
    >
      <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-2 lg:gap-16">

        {/* Left: the offer */}
        <div>
          <SectionHeader
            eyebrow="Start With A Sample"
            title={headline}
            accent={headlineAccent}
            layout="stack"
          />

          {/* This Reveal now has RevealItem children. Previously both Reveals in
              this file wrapped plain <div>s with no variants, so they mounted an
              extra motion element, a scroll listener and an IntersectionObserver
              to orchestrate exactly nothing. */}
          <Reveal>
            <RevealItem>
              <p className="font-body text-body text-mute">What happens next:</p>
              <ul className="mt-4 space-y-3">
                {bullets.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <ArrowRight aria-hidden="true" className="mt-1 h-4 w-4 shrink-0 text-faint" />
                    <span className="font-body text-body text-bone">{item}</span>
                  </li>
                ))}
              </ul>
            </RevealItem>

            {/* The two alternate channels, grouped under one rule. They used to
                be two more full-sentence buttons competing with the form's own
                submit — four differently-named exits from a single form. */}
            <RevealItem>
              <Rule className="mt-10" />

              <p className="mt-8 font-body text-body text-mute">Prefer to talk first?</p>
              <div className="mt-4">
                <CTA
                  href={`https://calendly.com/anish-kyrosity/30min?utm_source=website&utm_campaign=${source}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  tone="secondary"
                  arrow={false}
                  data-testid="calendly-cta"
                >
                  <Calendar aria-hidden="true" className="h-4 w-4" />
                  Book 30 minutes
                </CTA>
              </div>

              <p className="mt-8 font-body text-body text-mute">Prefer email?</p>
              <a
                href="mailto:contact@kyrosity.ai"
                className="mt-1 inline-flex min-h-11 items-center gap-2.5 font-body text-body-l text-bone transition-colors duration-hover ease-gate hover:text-gold"
              >
                <Mail aria-hidden="true" className="h-4 w-4" />
                contact@kyrosity.ai
              </a>
            </RevealItem>
          </Reveal>
        </div>

        {/* Right: the form. Deliberately NOT wrapped in a Reveal — the form is
            the section's primary action and must never start at opacity 0
            waiting on an observer that might not fire. */}
        {isSubmitted ? (
          <Panel className="flex h-full flex-col items-center justify-center text-center">
            {/* Neutral tick. At 48px in gold this was the largest gold object in
                the section and not clickable — it taught gold = "confirmed" on
                top of gold = "click", "required" and "focused". */}
            <CheckCircle aria-hidden="true" className="mb-5 h-10 w-10 text-mute" />
            <h3 className="mb-3 font-display text-h2 uppercase text-bone">Brief received.</h3>
            <p className="measure font-body text-body text-mute">
              We&rsquo;ll come back with a clear next step and what we&rsquo;d make.
            </p>
            {/* Gold is correct here — it is a button. min-h-11 gives it the touch
                target the old 11px underlined link did not have. */}
            <button
              type="button"
              onClick={() => setSubmitted(false)}
              className="mt-6 inline-flex min-h-11 items-center font-body text-body text-gold underline underline-offset-4 transition-colors duration-hover ease-gate hover:text-gold-hover hover:no-underline"
            >
              Send another brief
            </button>
          </Panel>
        ) : (
          <Panel>
            <p className="measure mb-6 font-body text-body text-mute">
              No deck needed. Tell us what needs to grow.
            </p>
            <form
              ref={formRef}
              onSubmit={handleSubmit}
              className="space-y-5"
              data-testid="contact-form"
              name="contact"
              noValidate
            >
              {/* Web3Forms honeypot — bots that tick it get discarded in handleSubmit */}
              <input
                type="checkbox"
                name="botcheck"
                tabIndex={-1}
                aria-hidden="true"
                className="hidden"
                style={{ display: "none" }}
              />

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <Field id="contact-name" label="Name" required error={errors.name}>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    autoComplete="name"
                    aria-required="true"
                    aria-invalid={Boolean(errors.name)}
                    aria-describedby={describedBy("contact-name")}
                    data-testid="contact-name-input"
                    className={fieldClass}
                  />
                </Field>

                <Field id="contact-email" label="Email" required error={errors.email}>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@company.com"
                    autoComplete="email"
                    inputMode="email"
                    spellCheck={false}
                    autoCapitalize="off"
                    aria-required="true"
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={describedBy("contact-email")}
                    data-testid="contact-email-input"
                    className={fieldClass}
                  />
                </Field>
              </div>

              <Field id="contact-company" label="Company" error={errors.company}>
                <input
                  id="contact-company"
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Company name"
                  autoComplete="organization"
                  aria-invalid={Boolean(errors.company)}
                  aria-describedby={describedBy("contact-company")}
                  data-testid="contact-company-input"
                  className={fieldClass}
                />
              </Field>

              <Field id="contact-brief" label="What should the sample sell?" error={errors.brief} hint="Optional. A product link and the audience you want is plenty.">
                <textarea
                  id="contact-brief"
                  name="brief"
                  value={formData.brief}
                  onChange={handleChange}
                  placeholder="A link to the product or app, the audience, or the growth question"
                  rows={3}
                  aria-required="false"
                  aria-invalid={Boolean(errors.brief)}
                  aria-describedby={describedBy("contact-brief")}
                  data-testid="contact-brief-textarea"
                  className={`${fieldClass} resize-y`}
                />
              </Field>

              {/* Keeps its own label. Every other ask on the site is
                  CONVERSION.label, but a submit button should describe the
                  submit, not the offer. Hand-composed rather than <CTA> because
                  CTA renders a link, and this has to be a real submit button —
                  the geometry and type below are ctaBase/ctaTones.primary. */}
              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="contact-submit-btn"
                className="flex min-h-12 w-full items-center justify-center gap-3 bg-gold px-8 font-label text-body font-medium uppercase tracking-action text-gold-on transition-colors duration-hover ease-gate hover:bg-gold-hover disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  "Send me the free sample"
                )}
              </button>

              {/* Secondary. As a second full-width gold bar directly under the
                  first, the failure state offered two equally loud primaries at
                  the exact moment the visitor needed to be told which one now
                  works. */}
              {submitFailed && (
                <CTA
                  href={fallbackMailto()}
                  tone="secondary"
                  arrow={false}
                  className="w-full"
                  data-testid="contact-mailto-fallback"
                >
                  <Mail aria-hidden="true" className="h-4 w-4" />
                  Email your brief instead
                </CTA>
              )}
            </form>
          </Panel>
        )}
      </div>
    </Section>
  );
};

export default Contact;
